import { Button, makeStyles } from '@material-ui/core'
import * as React from 'react'
import { useState } from 'react'
import PaletteCard from './PaletteCard'
import PaletteEditor from './PaletteEditor'
import { OrganizationSettings } from '../../dtos/OrganizationDTO'

const useStyles = makeStyles((theme) => {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      padding: theme.spacing(3),
      backgroundColor: theme.palette.neutrals[50],
    },
    palettesContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    actions: {
      display: 'flex',
      gap: theme.spacing(2),
    },
  }
})

interface PalettesContainerProps {
  palettes: { name: string; colors: string[] }[]
  onSelect: (value: string) => void
  selectedPalette: string
  customPalette?: OrganizationSettings['Typography']['ThemeCustomization']['ColorScheme']['WhiteMode']['CustomScheme']
  onCustomPaletteChange: (value: string[]) => void
  onCustomPaletteNameChange: (value: string) => void
}

const PalettesContainer: React.FC<PalettesContainerProps> = ({
  palettes,
  onSelect,
  selectedPalette,
  customPalette,
  onCustomPaletteChange,
  onCustomPaletteNameChange,
}) => {
  const classes = useStyles()
  const [isEditMode, setIsEditMode] = useState(false)

  const customColors = customPalette
    ? [
        customPalette.SecondaryColor?.Color || palettes[0].colors[0],
        customPalette.PrimaryColor || palettes[0].colors[1],
        customPalette.TertiaryColor?.Color || palettes[0].colors[2],
      ]
    : palettes[0].colors

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <p className='is-size-6'>
          Each scheme automatically generates Primary, Secondary, Tertiary, and Support colors in harmony
        </p>
        {isEditMode && (
          <div className={classes.actions}>
            <Button variant='outlined' onClick={() => setIsEditMode(false)}>
              Back
            </Button>
            <Button variant='contained' color='primary' onClick={() => setIsEditMode(false)}>
              Save
            </Button>
          </div>
        )}
      </div>
      {isEditMode ? (
        <PaletteEditor
          name={customPalette?.SchemeName || ''}
          onNameChange={onCustomPaletteNameChange}
          colors={customColors}
          onChange={onCustomPaletteChange}
        />
      ) : (
        <div className={classes.palettesContainer}>
          {palettes.map((palette) => (
            <PaletteCard
              key={palette.name}
              name={palette.name}
              colors={palette.colors}
              onSelect={() => onSelect(palette.name)}
              selected={selectedPalette === palette.name}
            />
          ))}
          <PaletteCard
            name={customPalette?.SchemeName || 'Custom'}
            colors={customColors}
            onSelect={() => onSelect('custom')}
            canEdit
            onEdit={() => setIsEditMode(true)}
            selected={selectedPalette === 'custom'}
          />
        </div>
      )}
    </div>
  )
}

export default PalettesContainer
